import React, { memo, useMemo } from "react";
import { Tooltip } from "antd";
import { EnvironmentOutlined, GlobalOutlined } from "@ant-design/icons";
import {
  formatDate,
  formatTime,
  isManualOffset,
  getTimezoneAbbreviation,
  getTimezoneOffsetLabel,
} from "../../utils/timezoneUtils";

const TimeDisplay = ({
  label,
  date,
  timezone,
  timeFormat = "24",
  dateFormat,
  primary = false,
  clockId,
}) => {
  const display = useMemo(() => {
    if (!timezone) return null;
    const manualOffset = isManualOffset(timezone);
    const abbreviation = getTimezoneAbbreviation(timezone, date);
    return {
      time: formatTime({ date, timezone, timeFormat }),
      seconds: formatTime({ date, timezone, timeFormat, includeSeconds: true }),
      date: formatDate({ date, timezone, dateFormat }),
      // Manual offsets are objects, which React cannot render as children.
      timezoneReference: manualOffset
        ? getTimezoneOffsetLabel(timezone, date)
        : typeof timezone === "string"
          ? timezone
          : "Not configured",
      // Numeric GMT/UTC offsets are already shown by the timezone selector.
      // Keep this space for meaningful abbreviations such as IST or BST only.
      timezoneLabel: manualOffset
        ? getTimezoneOffsetLabel(timezone, date)
        : /^(?:GMT|UTC)(?:[+-]\d)/i.test(abbreviation)
          ? ""
          : abbreviation,
      offset: getTimezoneOffsetLabel(timezone, date),
    };
  }, [date, dateFormat, timeFormat, timezone]);

  if (!display) {
    return (
      <div
        className={`time-display time-display--unconfigured time-display--${clockId}`}
        role="status"
      >
        <EnvironmentOutlined className="time-display__icon" aria-hidden="true" />
        <strong className="time-display__label">Local:</strong>
        <span className="time-display__not-configured">Not configured</span>
      </div>
    );
  }

  const tooltip = (
    <div className="time-display__tooltip">
      <strong>{primary ? "UTC" : "Local Time"}</strong>
      <span>{display.seconds}</span>
      {!primary && <span>{display.timezoneReference}</span>}
      {!primary && <span>{display.offset}</span>}
      <span>{display.date}</span>
    </div>
  );

  return (
    <Tooltip title={tooltip} placement="bottom" mouseEnterDelay={0.25}>
      <div
        className={`time-display time-display--${clockId} ${
          primary ? "time-display--primary" : ""
        }`}
      >
        {primary ? (
          <GlobalOutlined className="time-display__icon" aria-hidden="true" />
        ) : (
          <EnvironmentOutlined className="time-display__icon" aria-hidden="true" />
        )}
        <strong className="time-display__label">{label}:</strong>
        <span className="time-display__value">
          {display.time}
          {!primary && display.timezoneLabel && (
            <span className="time-display__timezone">{display.timezoneLabel}</span>
          )}
        </span>
      </div>
    </Tooltip>
  );
};

export default memo(TimeDisplay);
